/**
 * @file useMessageItem.js
 * @description 单条留言/评论：点赞、回复、删除、展开回复列表
 * @module features/message/composables/useMessageItem
 */

import { reactive, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { normalizeMessageFields } from '@/features/message/utils/normalizeMessageFields'
import {
  cancelMessageLikeApi,
  deleteMessageApi,
  fetchMessageReplyPageApi,
  insertMessageLikeApi,
  replyMessageApi,
} from '@/features/message/api/messageApi'

/**
 * @param {import('vue').Ref<Record<string, unknown>> | Record<string, unknown>} message
 * @param {(event: string, ...args: unknown[]) => void} emit
 */
export function useMessageItem(message, emit) {
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  const pageSize = ref(5)
  const showReplyBox = ref(false)
  const replyContent = ref('')
  const loadingReply = ref(false)
  const likeEffect = ref(false)
  const showAllReplies = ref(false)
  const replyContentMap = reactive({})
  const showReplyBoxMap = reactive({})
  const loadingRepliesMap = reactive({})
  const likeEffectMap = reactive({})

  function getMessage() {
    return message.value ?? message
  }

  function getAuthToken() {
    return user.value?.token || ''
  }

  function showLoginPrompt() {
    emit('show-popup-message', '请您登录后操作', 'login-required')
  }

  function checkLogin() {
    if (!user.value?.token) {
      showLoginPrompt()
      return false
    }
    return true
  }

  async function handleLikeMessage() {
    if (!checkLogin()) return

    const msg = getMessage()
    const isLike = msg.isMyLike
    const likeOrCancelUrl = isLike ? cancelMessageLikeApi : insertMessageLikeApi

    try {
      const response = await likeOrCancelUrl(
        { type: 'MESSAGE', targetId: msg.messageId },
        getAuthToken()
      )

      if (response.data.code === 200 || response.data.code === 0) {
        msg.isMyLike = !msg.isMyLike
        msg.likeCount = (msg.likeCount || 0) + (msg.isMyLike ? 1 : -1)
        if (!isLike) {
          triggerLikeEffect()
        }
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        emit('show-popup-message', response.data.msg || '点赞失败', 'error')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        emit(
          'show-popup-message',
          error.response?.data?.msg || '点赞请求失败',
          'error'
        )
      }
    }
  }

  async function likeReply(reply) {
    if (!checkLogin()) return

    const isLike = reply.isMyLike
    const likeOrCancelUrl = isLike ? cancelMessageLikeApi : insertMessageLikeApi

    try {
      const response = await likeOrCancelUrl(
        { type: 'MESSAGE', targetId: reply.messageId },
        getAuthToken()
      )

      if (response.data.code === 0 || response.data.code === 200) {
        reply.isMyLike = !reply.isMyLike
        reply.likeCount = (reply.likeCount || 0) + (reply.isMyLike ? 1 : -1)
        if (!isLike) {
          triggerLikeEffectForReply(reply.messageId)
        }
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        emit('show-popup-message', response.data.msg || '点赞失败', 'error')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        emit(
          'show-popup-message',
          error.response?.data?.msg || '点赞请求失败',
          'error'
        )
      }
    }
  }

  async function loadReplies() {
    const msg = getMessage()
    try {
      const response = await fetchMessageReplyPageApi(
        {
          messageId: msg.messageId,
          type: 'GUESTBOOK',
          pageNum: 1,
          pageSize: pageSize.value,
        },
        getAuthToken()
      )
      if (response.data.code === 200 || response.data.code === 0) {
        msg.replies = (response.data.contents || []).map((reply) =>
          normalizeMessageFields(reply)
        )
        msg.existReply = msg.replies.length > 0
        msg.replyCount = response.data.total || 0
      } else {
        console.error('加载回复失败:', response.data.msg)
      }
    } catch (error) {
      console.error('请求回复接口失败:', error)
    }
  }

  async function toggleCommentReplies() {
    const msg = getMessage()
    if (msg.showReplies) {
      msg.showReplies = false
      showAllReplies.value = false
    } else {
      msg.showReplies = true
      if (!msg.replies || msg.replies.length === 0) {
        await loadReplies()
      }
    }
  }

  function toggleReplyBox() {
    showReplyBox.value = !showReplyBox.value
    if (!showReplyBox.value) {
      replyContent.value = ''
    }
  }

  function toggleReplyBoxForReply(messageId) {
    if (!showReplyBoxMap[messageId]) {
      showReplyBoxMap[messageId] = true
      if (!replyContentMap[messageId]) {
        replyContentMap[messageId] = ''
      }
    } else {
      showReplyBoxMap[messageId] = false
      replyContentMap[messageId] = ''
    }
  }

  async function submitReply() {
    if (!checkLogin()) return
    if (!replyContent.value.trim()) {
      emit('show-popup-message', '请输入回复内容', 'error')
      return
    }
    if (loadingReply.value) return

    const msg = getMessage()
    try {
      loadingReply.value = true
      const response = await replyMessageApi(
        {
          content: replyContent.value.trim(),
          parentId: msg.messageId,
          type: 'GUESTBOOK',
        },
        getAuthToken()
      )

      if (response.data.code === 200 || response.data.code === 0) {
        emit('show-popup-message', response.data.msg || '回复成功', 'success')
        replyContent.value = ''
        showReplyBox.value = false

        if (response.data.content) {
          if (!msg.replies) {
            msg.replies = []
          }
          msg.replies.push(
            normalizeMessageFields({
              ...response.data.content,
              isMyLike: false,
              isBelongMe: true,
            })
          )
          if (msg.replyCount !== undefined) {
            msg.replyCount++
          } else {
            msg.replyCount = msg.replies.length
          }
          msg.existReply = true
          msg.showReplies = true
        } else {
          await loadReplies()
          msg.showReplies = true
        }
      } else if (response.data.code === 10474) {
                emit('show-login-popup')
      } else {
        emit('show-popup-message', response.data.msg || '回复失败', 'error')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        emit('show-popup-message', error.message || '回复失败', 'error')
      }
    } finally {
      loadingReply.value = false
    }
  }

  async function submitReplyForReply(reply) {
    if (!checkLogin()) return
    const content = replyContentMap[reply.messageId] || ''
    if (!content.trim()) {
      emit('show-popup-message', '请输入回复内容', 'error')
      return
    }

    const messageId = reply.messageId
    if (loadingRepliesMap[messageId]) return

    const msg = getMessage()
    try {
      loadingRepliesMap[messageId] = true
      const response = await replyMessageApi(
        {
          content: content.trim(),
          parentId: reply.messageId,
          type: 'GUESTBOOK',
        },
        getAuthToken()
      )

      if (response.data.code === 0 || response.data.code === 200) {
        replyContentMap[messageId] = ''
        showReplyBoxMap[messageId] = false

        if (response.data.content) {
          if (!msg.replies) {
            msg.replies = []
          }
          msg.replies.push(
            normalizeMessageFields({
              ...response.data.content,
              isMyLike: false,
              isBelongMe: true,
            })
          )
          if (msg.replyCount !== undefined) {
            msg.replyCount++
          } else {
            msg.replyCount = msg.replies.length
          }
          msg.existReply = true
          msg.showReplies = true
        } else {
          await loadReplies()
          msg.showReplies = true
        }
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        emit('show-popup-message', response.data.msg || '回复失败', 'error')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        emit(
          'show-popup-message',
          error.response?.data?.msg || '回复失败',
          'error'
        )
      }
    } finally {
      loadingRepliesMap[messageId] = false
    }
  }

  function toggleShowAllReplies() {
    showAllReplies.value = !showAllReplies.value
  }

  async function postDeleteMessage(messageId) {
    try {
      const response = await deleteMessageApi(messageId, getAuthToken())
      if (response.data.code === 200 || response.data.code === 0) {
        return { ok: true, msg: response.data.msg }
      }
      return { ok: false, msg: response.data.msg || '删除失败' }
    } catch (e) {
      return {
        ok: false,
        msg: e.response?.data?.msg || e.message || '删除失败',
      }
    }
  }

  async function deleteRootMessage() {
    if (!checkLogin()) return

    const msg = getMessage()
    const res = await postDeleteMessage(msg.messageId)
    if (!res.ok) {
      emit('show-popup-message', res.msg, 'error')
      return
    }
    emit('show-popup-message', '删除成功', 'success')
    emit('message-deleted', { messageId: msg.messageId })
  }

  async function deleteGuestbookReply(reply) {
    if (!checkLogin()) return

    const msg = getMessage()
    const res = await postDeleteMessage(reply.messageId)
    if (!res.ok) {
      emit('show-popup-message', res.msg, 'error')
      return
    }
    emit('show-popup-message', '删除成功', 'success')
    if (showReplyBoxMap[reply.messageId]) {
      showReplyBoxMap[reply.messageId] = false
    }
    delete replyContentMap[reply.messageId]
    await loadReplies()
    if (!msg.replies || msg.replies.length === 0) {
      msg.existReply = false
      msg.showReplies = false
    }
  }

  function triggerLikeEffect() {
    likeEffect.value = true
    setTimeout(() => {
      likeEffect.value = false
    }, 1000)
  }

  function triggerLikeEffectForReply(messageId) {
    likeEffectMap[messageId] = true
    setTimeout(() => {
      likeEffectMap[messageId] = false
    }, 1000)
  }

  return {
    showReplyBox,
    replyContent,
    loadingReply,
    likeEffect,
    showAllReplies,
    replyContentMap,
    showReplyBoxMap,
    loadingRepliesMap,
    likeEffectMap,
    handleLikeMessage,
    likeReply,
    toggleCommentReplies,
    toggleReplyBox,
    toggleReplyBoxForReply,
    submitReply,
    submitReplyForReply,
    toggleShowAllReplies,
    deleteRootMessage,
    deleteGuestbookReply,
  }
}
